import { ApplicationRef, Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, interval, map, Observable, startWith, switchMap, take } from 'rxjs';
import { AuthService } from './core/services/auth.service';
import { CampaignService } from './core/services/campaign.service';
import { Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { environment } from '@uw-env/environment';
import { CustomNavigationService } from './core/custom-navigation.service';
import { SwUpdate } from '@angular/service-worker';
import { SessionManagerService } from './core/services/session-manager.service';
import { RecaptchaService } from './core/services/recaptcha.service';
import { IdleLogoutService } from './core/services/idle-logout.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  siteType: string = '';
  private sessionInitialized = false;
  showRecaptchaBadge$: Observable<boolean>;

  // idleState = 'Not started.';
  // timedOut = false;

  constructor(
    private readonly _route: ActivatedRoute,
    private translate: TranslateService,
    private customNavigationService: CustomNavigationService,
    private readonly title: Title,
    protected _authService: AuthService,
    protected _campaignService: CampaignService,
    private _router: Router,
    private idle: Idle,
    private keepalive: Keepalive,
    private update: SwUpdate,
    private appRef: ApplicationRef,
    private sessionManager: SessionManagerService,
    private recaptchaService: RecaptchaService,
    private idleLogoutService: IdleLogoutService
  ) {
     this.updateClient();
     this.checkUpdate();
     this.idleLogoutService.init();
    //  // set idle parameters
    //  idle.setIdle(5); // how long can they be inactive before considered idle, in seconds
    //  idle.setTimeout(5); // how long can they be idle before considered timed out, in seconds
    //  idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // provide sources that will "interrupt" aka provide events indicating the user is active

    //  // do something when the user becomes idle
    //  idle.onIdleStart.subscribe(() => {
    //    this.idleState = "IDLE";
    //  });
    //  // do something when the user is no longer idle
    //  idle.onIdleEnd.subscribe(() => {
    //    this.idleState = "NOT_IDLE";
    //    console.log(`${this.idleState} ${new Date()}`)
    //    this.countdown = null;
    //    cd.detectChanges(); // how do i avoid this kludge?
    //  });
    //  // do something when the user has timed out
    //  idle.onTimeout.subscribe(() => this.idleState = "TIMED_OUT");
    //  // do something as the timeout countdown does its thing
    //  idle.onTimeoutWarning.subscribe(seconds => this.countdown = seconds);

     // set keepalive parameters, omit if not using keepalive
     this.keepalive.interval(environment.ngIdleConfig.keepaliveInterval); // will ping at this interval while not idle, in seconds 10 mins

     this.keepalive.onPing.pipe(
      filter(_ => !!localStorage.getItem('token')),
      switchMap(_ => this._authService.refreshToken(localStorage.getItem('token')))
    ).subscribe(
      (token) => {
        localStorage.setItem('token', token);
      },
      err => {
        const isSSOUser = localStorage.getItem('isSSOUser') === 'true';
        const ssoProviderId = localStorage.getItem('ssoProviderId');
        const ssoProtocol = localStorage.getItem('ssoProtocol');
        this._authService.clearStorage();
        if (isSSOUser && ssoProviderId && ssoProtocol) {
          // Re-initiate SSO authentication — if the SSO session is still active the
          // identity provider will silently redirect back and the user continues
          // without seeing the login page.
          this._authService.authenticateViaSSO(ssoProviderId, ssoProtocol);
        } else {
          this.customNavigationService.navigate(['main/login']);
        }
      }
    );

    this.reset();
  }

  reset() {
    this.idle.watch();
    // this.idleState = 'Started.';
    // this.timedOut = false;
  }

  ngOnInit() {
    const lang = localStorage.getItem('lang');
    this.siteType = environment.mode;

    this.showRecaptchaBadge$ = combineLatest([
      this._authService.userLoggedIn$,
      this._router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(e => e.urlAfterRedirects),
        startWith(this._router.url)
      )
    ]).pipe(
      map(([loginState, url]) => !loginState?.loggedIn && !this.isAuthPage(url))
    );
    this.translate.use(lang ?? 'en');

    // Initialize session on first navigation based on auth state and page type
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      take(1)
    ).subscribe((event) => {
      this.initSession((event as NavigationEnd).urlAfterRedirects);
    });

    // Re-initialize session without reCAPTCHA after login, only if not already initialized for this user
    this._authService.getUserState().subscribe((user) => {
      if (!user) {
        // Reset on logout so the next login triggers a fresh session init
        this.sessionInitialized = false;
        return;
      }
      if (this.sessionInitialized) return;
      this.sessionInitialized = true;
      this.sessionManager.initializeSession(false).subscribe({
        error: (err) => console.error('SessionManager: Session init failed post-login (non-blocking):', err)
      });
    });

    if (!localStorage.getItem('token')) {
      // The base url is stored here as this will be used to set the href param on
      //  windows object when switching the lang to reload the page with correct lang converted url.
      if (this.isFrenchURL()) {
        localStorage.setItem('baseUrl', environment.frenchUrl);
      } else {
        localStorage.setItem('baseUrl', environment.englishUrl);
      }
    }

    if (this.isFrenchURL() && lang != 'en') {
      this.translate.use('fr');
      this.translate.currentLang = 'fr';
      // localStorage.setItem('lang', 'fr');
    } else {
      // this.translate.use('en');
      // this.translate.currentLang = 'en';
      // localStorage.setItem('lang', 'en');
    }
    this.translate.get('title').subscribe((translatedTitle) => {
      this.title.setTitle(translatedTitle);
    });
  }

  updateClient() {
    if (!this.update.isEnabled) {
      return;
    }

    this.update.versionUpdates.subscribe((event) => {
      if (event.type == 'VERSION_DETECTED') {
        this.update.activateUpdate().then(() => location.reload());
      }
    });
  }

  checkUpdate() {
    if (!this.update.isEnabled) return;

    this.appRef.isStable
      .pipe(
        take(1)
      )
      .subscribe((counter) => {
        this.update
          .checkForUpdate();
      });

    this.appRef.isStable
      .pipe(
        take(1),
        switchMap(() => interval(300000))
      )
      .subscribe((counter) => {
        this.update
          .checkForUpdate();
      });
  }

  ngOnDestroy() {
    localStorage.removeItem('isLiteCampaignBiLingual');
    localStorage.removeItem('ignoreLoggedInUserLangPref');
    // Clean up session manager resources
    this.sessionManager.destroySession();
  }

  private initSession(url: string): void {
    const isLoggedIn = !!localStorage.getItem('token');
    if (isLoggedIn) {
      this.sessionInitialized = true;
      this.sessionManager.initializeSession(false).subscribe({
        error: (err) => console.error('SessionManager: Session init failed (non-blocking):', err)
      });
    } else if (!this.isAuthPage(url)) {
      this.sessionInitialized = true;
      this.recaptchaService.init(environment.recaptchaSiteKey, this.translate.currentLang ?? 'en');
      this.sessionManager.initializeSession(true).subscribe({
        error: (err) => console.error('SessionManager: Session init failed (non-blocking):', err)
      });
    }
  }

  private isAuthPage(url: string): boolean {
    const authPaths = [
      '/login',                       // EN regular + sidebar login
      '/sso-login',                   // EN SSO login
      '/connexion',                   // FR login
      '/sso-connexion',               // FR SSO login
      '/forgotpassword',
      '/forgot-password',
      '/mot-de-passe-oublie',         // FR forgot password
      '/reset-password',
      '/change-password',
      '/password-expired',
      '/mfa',                         // MFA verification
      '/id-validate',                 // Registration token verification
      '/user-registration-feedback',  // Post-registration
      '/retour-inscription-utilisateur', // FR post-registration
    ];
    return authPaths.some(path => url.includes(path));
  }

  private isFrenchURL(): boolean {
    const currentURL = window.location.href;
    // Check if the URL contains '/fr/' indicating a French URL
    return currentURL.includes(environment.frenchUrl);
  }
}
